import React, { useState, useEffect } from 'react';
import Drawer from 'react-modern-drawer';
import { useAuth } from 'app/modules/auth';
import DrawerHeader from 'app/components/Drawer/DrawerHeader';
import {
  doc,
  collection,
  onSnapshot,
  setDoc,
  updateDoc,
  serverTimestamp,
  query,
  where,
  getDocs,
  deleteField,
} from 'firebase/firestore';
import db from 'services/firebase';
import { KTIcon, toAbsoluteUrl } from '_metronic/helpers';
import { showErrorToast, showSuccessToast } from 'app/components/ShowToast';
import { Employee, Integration, ReviewData, ReviewerData, SystemData, ViewPersonnelProps } from '../../utils/DrawersUtils';
import Input from 'app/components/Input';

const ReviewDrawer: React.FC<ViewPersonnelProps> = ({ isOpen, onClose, reviewId }) => {
  const { currentUser } = useAuth();
  const [reviewName, setReviewName] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedSystems, setSelectedSystems] = useState<string[]>([]);
  const [reviewer, setReviewer] = useState<ReviewerData | null>(null);
  const [riskScore, setRiskScore] = useState('');
  const [accessIntegration, setAccessIntegration] = useState('');
  const [systems, setSystems] = useState<SystemData[]>([]);
  const [employees, setEmployees] = useState<Employee[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [nameError, setNameError] = useState<string | null>(null);
  const [dueDate, setDueDate] = useState('');
  const [existingSystems, setExistingSystems] = useState<Record<string, any>>({});
  const [isSubmitting, setIsSubmitting] = useState(false);

  const isEditMode = !!reviewId;

  useEffect(() => {
    if (isEditMode && currentUser?.tenantId && reviewId && isOpen && !isSubmitting) {
      const reviewRef = doc(db, 'tenants', currentUser.tenantId, 'reviews', reviewId);
      const unsubscribe = onSnapshot(reviewRef, (reviewSnap) => {
        if (reviewSnap.exists()) {
          const reviewData = reviewSnap.data() as ReviewData;
          setReviewName(reviewData.reviewName);
          setReviewer(reviewData.reviewer);
          setDueDate(reviewData.dueDate);
          setSelectedSystems(Object.keys(reviewData.systems));
          setExistingSystems(reviewData.systems || {});
        } else {
          showErrorToast({ message: 'Review not found' });
          handleCloseDrawer();
        }
      }, (error) => {
        showErrorToast({ message: 'Failed to load review data' });
      });
      return () => unsubscribe();
    }
  }, [isEditMode, reviewId, currentUser, isOpen, isSubmitting]);

  useEffect(() => {
    const getIntegrations = () => {
      try {
        const tenantId = currentUser?.tenantId || '';
        if (!tenantId) return;

        const tenantRef = doc(db, 'tenants', tenantId);
        const integrationsRef = collection(tenantRef, 'vendors');

        const unsubscribe = onSnapshot(integrationsRef, (snapshot) => {
          const integrationData = snapshot.docs
          .filter((item) => !item.data().archived)
           .filter((item) => item.data().vendor_name !== 'Github Dependabot' && item.data().vendor_name !== 'Aikido')
          .map((item) => {
            const data = item.data();
            return {
              id: item.id,
              vendor_name: data.vendor_name || '',
              website_url: data.website_url || '',
              risk_level: data.risk_level || '',
              img_path: data.logo && data.logo[0]?.url ? data.logo[0].url : '',
              createdAt: data.created_at || null,
              status: data.status || 'Connected',
              tenantId: data.tenantId || tenantId,
              uid: data.uid || '',
            } as Integration;
          });

          const mappedSystems: SystemData[] = integrationData.map((integration) => ({
            id: integration.id,
            name: integration.vendor_name,
            domain: integration.website_url,
            icon: integration.img_path,
            riskScore: integration.risk_level,
            integrationStatus: integration.status.toLowerCase() as 'connected' | 'disconnected',
            status: integration.status,
          }));

          setSystems(mappedSystems);
        });

        return unsubscribe;
      } catch (error) {
        console.error('Error getting integrations:', error);
      }
    };

    const getEmployees = () => {
      try {
        const tenantId = currentUser?.tenantId || '';
        if (!tenantId) return;

        const tenantRef = doc(db, 'tenants', tenantId);
        const employeesRef = collection(tenantRef, 'employees');

        const unsubscribe = onSnapshot(employeesRef, (snapshot) => {
          const employeeData = snapshot.docs
            .map((doc) => ({
              id: doc.id,
              full_name: doc.data().full_name || '',
              end_date: doc.data().end_date,
              role: doc.data().role,
              email: doc.data().email,
            }))
            .filter((employee) => !employee.end_date);
          setEmployees(employeeData);
        });

        return unsubscribe;
      } catch (error) {
        console.error('Error getting employees:', error);
      }
    };

    const unsubscribeIntegrations = getIntegrations();
    const unsubscribeEmployees = getEmployees();
    return () => {
      if (unsubscribeIntegrations) unsubscribeIntegrations();
      if (unsubscribeEmployees) unsubscribeEmployees();
    };
  }, [currentUser]);

  const filteredSystems = systems.filter(
    (system) =>
      [system.name, system.domain, system.riskScore].some(
        (field) => field && field.toLowerCase().includes(searchTerm.trim().toLowerCase())
      ) &&
      (!riskScore || system.riskScore.toLowerCase() === riskScore.toLowerCase()) &&
      (!accessIntegration || system.integrationStatus === accessIntegration)
  );

  const handleSystemSelect = (systemId: string) => {
    setSelectedSystems((prev) =>
      prev.includes(systemId) ? prev.filter((id) => id !== systemId) : [...prev, systemId]
    );
  };

  const handleSelectAll = () => {
    if (selectedSystems.length === filteredSystems.length) {
      setSelectedSystems([]);
    } else {
      setSelectedSystems(filteredSystems.map((system) => system.id));
    }
  };

  const checkDuplicateReviewName = async (name: string): Promise<boolean> => {
    if (!currentUser?.tenantId || !name) return false;

    try {
      const tenantId = currentUser.tenantId;
      const reviewsRef = collection(db, 'tenants', tenantId, 'reviews');
      const q = query(reviewsRef, where('reviewName', '==', name.trim()));
      const querySnapshot = await getDocs(q);
      return isEditMode ? querySnapshot.docs.some((doc) => doc.id !== reviewId) : !querySnapshot.empty;
    } catch (error) {
      return false;
    }
  };

  const handleReviewNameChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const newName = e.target.value;
    setReviewName(newName);

    if (newName.trim()) {
      const isDuplicate = await checkDuplicateReviewName(newName);
      if (isDuplicate) {
        setNameError('A review with this name already exists. Please choose a different name.');
      } else {
        setNameError(null);
      }
    } else {
      setNameError('Review name cannot be empty.');
    }
  };

  const handleDueDateChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setDueDate(e.target.value);
  };

  const handleCloseDrawer = () => {
    // setReviewName('');
    // setReviewer(null); 
    // setDueDate('');
    setSelectedSystems([]);
    setExistingSystems({});
    setNameError(null);
    setSearchTerm('');
    setRiskScore('');
    setAccessIntegration('');
    setIsSubmitting(false);
    onClose();
  };

  const handleSubmit = async () => {
    if (!currentUser?.tenantId || selectedSystems.length === 0 || !reviewName || !reviewer || !dueDate) {
      showErrorToast({ message: 'Please fill in all required fields' });
      return;
    }

    if (nameError) {
      return;
    }

    setIsLoading(true);
    setIsSubmitting(true);

    try {
      const tenantId = currentUser.tenantId;
      const reviewRef = isEditMode && reviewId ? doc(db, 'tenants', tenantId, 'reviews', reviewId) : doc(collection(db, 'tenants', tenantId, 'reviews'));

      const systemsMap = selectedSystems.reduce((acc, systemId) => {
        const system = systems.find((s) => s.id === systemId);
        if (system) {
          acc[systemId] = {
            name: system.name,
            domain: system.domain,
            riskScore: system.riskScore,
            integrationStatus: system.integrationStatus,
            icon: system.icon,
            status: existingSystems[systemId]?.status || 'Pending',
          };
        }
        return acc;
      }, {} as Record<string, { name: string; domain: string; riskScore: string; integrationStatus: string; icon: string; status:string; }>);

      if (isEditMode && reviewId) {
        const existingSystemIds = Object.keys(existingSystems);
        const systemsToRemove = existingSystemIds.filter((id) => !selectedSystems.includes(id));
        if (systemsToRemove.length > 0) {
          const removePayload = systemsToRemove.reduce((acc, systemId) => {
            acc[`systems.${systemId}`] = deleteField();
            return acc;
          }, {} as Record<string, any>);
          await updateDoc(reviewRef, removePayload);
        }

        const updatePayload = {
          reviewName,
          updatedAt: serverTimestamp(),
          reviewer,
          dueDate,
          tenantId,
          createdBy: currentUser.uid,
          systems: systemsMap, 
        };
        await setDoc(reviewRef, updatePayload, { merge: true });
      } else {
        const createPayload = {
          reviewName,
          createdAt: serverTimestamp(),
          updatedAt: serverTimestamp(),
          reviewer,
          status: 'Pending',
          systems: systemsMap,
          dueDate,
          tenantId,
          createdBy: currentUser.uid,
        };
        await setDoc(reviewRef, createPayload);
      }

      showSuccessToast({ message: isEditMode ? 'Review updated successfully' : 'Review created successfully' });
      handleCloseDrawer();
    } catch (error) {
      showErrorToast({ message: isEditMode ? 'Failed to update review' : 'Failed to create review' });
    } finally {
      setIsLoading(false);
      setIsSubmitting(false);
    }
  };

  const getIntegrationStatusIcon = (status: 'connected' | 'disconnected') => {
    return status === 'connected' ? (
      <span className='ki-duotone ki-check-circle text-success fs-2' style={{ marginLeft: 20 }}>
        <span className='path1'></span>
        <span className='path2'></span>
      </span>
    ) : (
      <span className='ki-duotone ki-cross-circle fs-2' style={{ marginLeft: 20 }}>
        <span className='path1'></span>
        <span className='path2'></span>
      </span>
    );
  };

  return (
    <Drawer open={isOpen} onClose={handleCloseDrawer} direction='right' className='' size='45%'>
      <DrawerHeader title={isEditMode ? 'Edit Review' : 'Create Review'} onClose={handleCloseDrawer} />
      <div className='p-6 scroll-y overflow-auto' tabIndex={-1} style={{ height: 'calc(100vh - 90px)' }}>
      <div className='mb-4'>
        <Input
          label='Review name'
          placeholder='Enter review name'
          type='text'
          name='reviewName'
          value={reviewName}
          onChange={handleReviewNameChange}
          autoComplete='off'
          error={nameError || undefined}
          required
          style={{ height: '40px' }}
        />
      </div>
        <div className='mb-4'>
          <label className='form-label text-gray-900 fw-semibold fs-6 mb-2 required'>Reviewer</label>
          <select
            className='form-select'
            value={reviewer?.id || ''}
            onChange={(e) => {
              const selectedEmployee = employees.find(emp => emp.id === e.target.value);
              if (selectedEmployee) {
                setReviewer({
                  id: selectedEmployee.id,
                  name: selectedEmployee.full_name,
                  email: selectedEmployee.email || ''
                });
              }
            }}
            style={{ height: '40px' }}
          >
            <option value=''>Select Reviewer</option>
            {employees
            .filter((user) => user.role === 'Admin' || user.role === 'Owner')
            .map((employee) => (
              <option key={employee.id} value={employee.id}>
                {employee.full_name}
              </option>
            ))}
          </select>
        </div>
        <div className='mb-4'>
          <label className='form-label text-gray-900 fw-semibold fs-6 mb-2 required'>Due Date</label>
          <input
            placeholder='Due Date'
            className='form-control p-2'
            type='date'
            name='dueDate'
            autoComplete='off'
            onChange={handleDueDateChange}
            value={dueDate}
            disabled={currentUser?.role === 'Auditor'}
            style={{ height: '40px' }}
          />
        </div>

        <div className='mb-2'>
          <p className='text-gray-900 fw-semibold mb-1 required'>Select the systems you want to review.</p>
        </div>
        <div className='row'>
          <div className='col-md-6 ps-3 pe-0'>
            <div className='d-flex align-items-center position-relative'>
              <KTIcon iconName='magnifier' className='fs-3 position-absolute ms-4' />
              <input
                type='text'
                data-kt-user-table-filter='search'
                className='form-control ps-12'
                placeholder='Search systems...'
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                style={{ height: '38px' }}
              />
            </div>
          </div>
          <div className='col-md-3 ps-2 pe-0'>
            <select
              className='form-select fs-6'
              value={riskScore}
              onChange={(e) => setRiskScore(e.target.value)}
              style={{ height: '38px' }}
            >
              <option value=''>Risk score</option>
              <option value='low'>Low</option>
              <option value='medium'>Medium</option>
              <option value='high'>High</option>
            </select>
          </div>
          <div className='col-md-3 pe-3 ps-2'>
            <select
              className='form-select fs-6'
              value={accessIntegration}
              onChange={(e) => setAccessIntegration(e.target.value)}
              style={{ height: '38px' }}
            >
              <option value=''>Status</option>
              <option value='connected'>Connected</option>
              <option value='disconnected'>Disconnected</option>
            </select>
          </div>
        </div>
        <div className='py-3 mt-2'>
          <table className='table table-row-dashed table-row-gray-300 gy-5'>
            <thead>
              <tr className='fw-bold fs-7 text-gray-700'>
                <th className='min-w-20px'>
                  <input
                    type='checkbox'
                    className='form-check-input cursor-pointer form-check-primary'
                    checked={selectedSystems.length === filteredSystems.length && filteredSystems.length > 0}
                    onChange={handleSelectAll}
                    style={{ width: '20px', height: '20px' }}
                  />
                </th>
                <th className='min-w-50px'>SYSTEM</th>
                <th className='min-w-50px'>RISK SCORE</th>
                <th className='min-w-50px'>STATUS</th>
              </tr>
            </thead>
            <tbody>
              {filteredSystems.length > 0 ? (
                filteredSystems.map((system) => (
                  <tr key={system.id}>
                    <td>
                      <input
                        className='form-check-input cursor-pointer form-check-primary mt-2'
                        type='checkbox'
                        checked={selectedSystems.includes(system.id)}
                        onChange={() => handleSystemSelect(system.id)}
                        style={{ width: '20px', height: '20px' }}
                        disabled={existingSystems[system.id]?.status === 'Completed'}
                      />
                    </td>
                    <td>
                      <div className='d-flex align-items-center'>
                        <img
                          src={system.icon}
                          alt={system.name}
                          className='me-4'
                          style={{ width: '24px', height: '24px' }}
                        />
                        <div>
                          <div className='fw-semibold text-gray-900'>{system.name}</div>
                          <div className='text-gray-800 fs-7'>{system.domain}</div>
                        </div>
                      </div>
                    </td>
                    <td>
                      {system.riskScore ? (
                        <span
                          className={`badge fs-7 ${
                            system.riskScore === 'High'
                              ? 'badge-danger'
                              : system.riskScore === 'Medium'
                              ? 'badge-warning'
                              : 'badge-primary'
                          }`}
                          style={{
                            color:
                              system.riskScore === 'High'
                                ? '#EE3043'
                                : system.riskScore === 'Medium'
                                ? '#FFA800'
                                : '#0E9F6E',
                            background:
                              system.riskScore === 'High'
                                ? '#FFF5F8'
                                : system.riskScore === 'Medium'
                                ? '#FFF8DD'
                                : '#E8F8F4',
                          }}
                        >
                          {system.riskScore}
                        </span>
                      ) : (
                        '--'
                      )}
                    </td>
                    <td className='text-center'>
                      <div className='d-flex align-items-start'>{getIntegrationStatusIcon(system.integrationStatus)}</div>
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan={4} className='text-center text-muted'>
                    <div className='m-20'>
                      <img src={toAbsoluteUrl('/media/buttons/emptystate.svg')} alt='' />
                      <div className='m-5 fs-5'>
                        <p style={{ fontWeight: '600' }}>No systems match your search.</p>
                      </div>
                    </div>
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
      <div
        className='offcanvas-footer d-flex justify-content-end border-top py-4 px-6'
        style={{ position: 'sticky', bottom: 0, backgroundColor: 'white' }}
      >
        <div className='d-flex justify-content-end align-items-center'>
          {isLoading ? (
            <button
              className='btn btn-sm fw-semibold px-6 fs-6'
              type='submit'
              style={{ backgroundColor: '#0E9F6E', color: '#FFFFFF', height: '40px' }}
              onMouseEnter={(e: React.MouseEvent<HTMLElement>) =>
                (e.currentTarget.style.backgroundColor = '#148363')
              }
              onMouseLeave={(e: React.MouseEvent<HTMLElement>) =>
                (e.currentTarget.style.backgroundColor = '#0E9F6E')
              }
              disabled
            >
              {isEditMode ? 'Updating...' : 'Creating...'}
              <span className='spinner-border spinner-border-sm align-middle'></span>
            </button>
          ) : (
            <>
              <button
                className='btn btn-sm btn-light fw-semibold me-4 fs-6'
                onClick={handleCloseDrawer}
                aria-label='Close'
                style={{ height: '40px' }}
              >
                Cancel
              </button>
              <button
                className='btn btn-sm fw-semibold fs-6'
                onClick={handleSubmit}
                style={{ backgroundColor: '#0E9F6E', color: '#FFFFFF', height: '40px' }}
                onMouseEnter={(e: React.MouseEvent<HTMLElement>) =>
                  (e.currentTarget.style.backgroundColor = '#148363')
                }
                onMouseLeave={(e: React.MouseEvent<HTMLElement>) =>
                  (e.currentTarget.style.backgroundColor = '#0E9F6E')
                }
                disabled={isLoading || selectedSystems.length === 0 || !reviewName || !reviewer || !dueDate || !!nameError}
              >
                {isEditMode ? 'Update Review' : 'Create Review'}
              </button>
            </>
          )}
        </div>
      </div>
    </Drawer>
  );
};

export default ReviewDrawer;